import React from 'react';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  loading = false,
  icon,
  iconPosition = 'left',
  className = '',
  ...props 
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-1)',
    fontFamily: 'var(--font-family)',
    fontWeight: 'var(--font-weight-normal)',
    lineHeight: 'var(--line-height-tight)',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    transition: 'all 0.15s ease',
    textDecoration: 'none',
    position: 'relative',
    overflow: 'hidden',
    outline: 'none'
  };

  const variants = {
    primary: {
      backgroundColor: 'var(--color-primary)',
      color: 'white',
      '&:hover': {
        backgroundColor: 'var(--color-primary-dark)',
        opacity: 0.9
      },
      '&:active': {
        opacity: 0.8,
        transform: 'scale(0.98)'
      }
    },
    secondary: {
      backgroundColor: 'transparent',
      color: 'var(--color-gray-700)',
      border: '1px solid var(--color-gray-300)',
      '&:hover': {
        backgroundColor: 'var(--color-gray-50)',
        borderColor: 'var(--color-gray-400)'
      },
      '&:active': {
        backgroundColor: 'var(--color-gray-100)',
        transform: 'scale(0.98)'
      }
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'var(--color-gray-600)',
      '&:hover': {
        backgroundColor: 'var(--color-gray-100)',
        color: 'var(--color-gray-800)'
      },
      '&:active': {
        backgroundColor: 'var(--color-gray-200)',
        transform: 'scale(0.98)'
      }
    },
    danger: {
      backgroundColor: 'var(--color-error)',
      color: 'white',
      '&:hover': {
        opacity: 0.9
      },
      '&:active': {
        opacity: 0.8,
        transform: 'scale(0.98)'
      }
    },
    success: {
      backgroundColor: 'var(--color-success)',
      color: 'white',
      '&:hover': {
        opacity: 0.9
      },
      '&:active': {
        opacity: 0.8,
        transform: 'scale(0.98)'
      }
    }
  };

  // Touch-friendly sizes - minimum 44px for primary interactive sizes
  const sizes = {
    xs: {
      padding: 'var(--space-1) var(--space-2)',
      fontSize: 'var(--font-size-xs)',
      minHeight: '28px'
    },
    sm: {
      padding: 'var(--space-2) var(--space-3)',
      fontSize: 'var(--font-size-sm)',
      minHeight: '36px'
    },
    md: {
      padding: 'var(--space-2) var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      minHeight: '44px' // Touch-friendly minimum
    },
    lg: {
      padding: 'var(--space-3) var(--space-5)',
      fontSize: 'var(--font-size-base)',
      minHeight: '48px'
    },
    xl: {
      padding: 'var(--space-4) var(--space-6)',
      fontSize: 'var(--font-size-lg)',
      minHeight: '52px'
    }
  };

  const buttonStyles = {
    ...baseStyles,
    ...variants[variant],
    ...sizes[size],
    opacity: disabled || loading ? 0.6 : 1,
    ...props.style
  };

  const iconElement = icon && (
    <span style={{ 
      display: 'flex', 
      alignItems: 'center',
      fontSize: size === 'xs' ? '12px' : size === 'sm' ? '13px' : size === 'md' ? '14px' : size === 'lg' ? '15px' : '16px'
    }}>
      {icon}
    </span>
  );

  return (
    <button
      {...props}
      disabled={disabled || loading}
      style={buttonStyles}
      className={className}
    >
      {loading && (
        <span style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: size === 'xs' ? '10px' : size === 'sm' ? '12px' : '14px',
          height: size === 'xs' ? '10px' : size === 'sm' ? '12px' : '14px',
          border: '1.5px solid transparent',
          borderTop: '1.5px solid currentColor',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      )}
      
      <span style={{ 
        opacity: loading ? 0 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)'
      }}>
        {icon && iconPosition === 'left' && iconElement}
        {children}
        {icon && iconPosition === 'right' && iconElement}
      </span>
      
      <style>{`
        @keyframes spin {
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default Button;
